import { useState } from "react";

/**
 * Custom hook to manage drag and drop logic for module containers
 * @param {Object} adminData - Current admin data state
 * @param {Function} setAdminData - State setter for admin data
 * @param {Function} loadModule - Function to load a module
 * @param {Function} unloadModule - Function to unload a module
 */
export function useDragDrop(adminData, setAdminData, loadModule, unloadModule) {
    const [activeModule, setActiveModule] = useState(null);
    const [activeContainer, setActiveContainer] = useState(null);

    const handleDragStart = (event) => {
        const { active } = event;

        let matching = adminData.activeModules.find(m => m.manifest.moduleId === active.id);
        let container = 'active-module-container';

        if (!matching) {
            matching = adminData.inactiveModules.find(m => m.manifest.moduleId === active.id);
            container = 'inactive-module-container';
        }

        if (!matching) {
            console.error("No matching module found with ID " + active.id);
        } else {
            setActiveModule(matching);
            setActiveContainer(container);
        }
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveModule(null);
        setActiveContainer(null);

        if (!over) return;

        const activeId = active.id;
        const overId = over.id;

        // Determine which containers we're working with
        const activeInInactive = adminData.inactiveModules.some(m => m.manifest.moduleId === activeId);
        const activeInActive = adminData.activeModules.some(m => m.manifest.moduleId === activeId);

        const overIsContainer = overId === 'inactive-module-container' || overId === 'active-module-container';
        const overInInactive = adminData.inactiveModules.some(m => m.manifest.moduleId === overId);
        const overInActive = adminData.activeModules.some(m => m.manifest.moduleId === overId);

        setAdminData((prev) => {
            let updatedInactive = [...prev.inactiveModules];
            let updatedActive = [...prev.activeModules];
            let movedModule = null;

            // Find and remove the dragged module from its source
            if (activeInInactive) {
                const index = updatedInactive.findIndex(m => m.manifest.moduleId === activeId);
                movedModule = updatedInactive[index];
                updatedInactive.splice(index, 1);
            } else if (activeInActive) {
                const index = updatedActive.findIndex(m => m.manifest.moduleId === activeId);
                movedModule = updatedActive[index];
                updatedActive.splice(index, 1);
            }

            if (!movedModule) return prev;

            // Determine destination
            let targetIsActive = false;
            let insertIndex = -1;

            if (overIsContainer) {
                // Dropped on container itself - add to end
                targetIsActive = overId === 'active-module-container';
                insertIndex = targetIsActive ? updatedActive.length : updatedInactive.length;
            } else if (overInActive) {
                // Dropped on a module in active container
                targetIsActive = true;
                insertIndex = updatedActive.findIndex(m => m.manifest.moduleId === overId);
            } else if (overInInactive) {
                // Dropped on a module in inactive container
                targetIsActive = false;
                insertIndex = updatedInactive.findIndex(m => m.manifest.moduleId === overId);
            }

            // Insert at the target position
            if (targetIsActive) {
                updatedActive.splice(insertIndex, 0, movedModule);
                // Call load if moving from inactive to active
                if (activeInInactive) {
                    loadModule(activeId);
                }
            } else {
                updatedInactive.splice(insertIndex, 0, movedModule);
                // Call unload if moving from active to inactive
                if (activeInActive) {
                    unloadModule(activeId);
                }
            }

            return {
                ...prev,
                inactiveModules: updatedInactive,
                activeModules: updatedActive
            };
        });
    };

    return {
        activeModule,
        activeContainer,
        handleDragStart,
        handleDragEnd
    };
}